import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppThunk } from "../app/store";
import { Links } from "parse-link-header";
import { QuoteQueryParams } from "../api/Quote/QuoteQueryParams";
import { EQuoteOrderableField } from "../api/Quote/EQuoteOrderableField";
import { Quote, QuoteSearchResponse } from "../api/Quote/Quote";
import { getRelatedQuotes, searchQuotes } from "../api/Quote/QuoteApi";

interface QuotesState {
  quotesById: Record<string, QuoteSearchResponse>;
  currentPageQuotes: string[];
  relatedQuotes: Quote[];

  isLoading: boolean;
  latestRequestId: string | null;
  error: string | null;

  queryParams: QuoteQueryParams;
  pageCount: number;
  pageLinks: Links | null;
  total: number;
}

const initialState: QuotesState = {
  quotesById: {},
  currentPageQuotes: [],
  relatedQuotes: [],
  isLoading: false,
  latestRequestId: null,
  error: null,
  pageCount: 0,
  total: 0,
  pageLinks: {},
  queryParams: {
    page: 1,
    order: EQuoteOrderableField.Date,
    ascending: false
  }
};

function startLoading(state: QuotesState, action: PayloadAction<string>) {
  state.isLoading = true;
  state.latestRequestId = action.payload;
}

function loadingFailed(state: QuotesState, action: PayloadAction<{ requestId: string; error: string }>) {
  const { requestId, error } = action.payload;
  if (state.latestRequestId === requestId) {
    state.isLoading = false;
    state.error = error;
  }
}

const quotes = createSlice({
  name: "quotes",
  initialState,
  reducers: {
    getQuotesStart: startLoading,
    getQuotesSuccess(state, action: PayloadAction<{ quotes: QuoteSearchResponse[]; requestId: string }>) {
      const { requestId, quotes } = action.payload;
      if (state.latestRequestId === requestId) {
        state.isLoading = false;
        state.error = null;

        quotes.forEach((q) => {
          state.quotesById[q.quoteId] = q;
        });

        state.currentPageQuotes = quotes.map((q) => q.quoteId);
      }
    },
    getQuotesFailure: loadingFailed,
    getRelatedQuotesStart: startLoading,
    getRelatedQuotesSuccess(state, { payload }: PayloadAction<Quote[]>) {
      state.isLoading = false;
      state.error = null;
      payload.forEach((q) => {
        state.relatedQuotes.push(q);
      });
    },
    getRelatedQuotesFailure: loadingFailed,
    setQueryParams(state, { payload }: PayloadAction<QuoteQueryParams>) {
      state.queryParams = payload;
    }
  }
});

export const {
  getQuotesStart,
  getQuotesSuccess,
  getQuotesFailure,
  getRelatedQuotesStart,
  getRelatedQuotesSuccess,
  getRelatedQuotesFailure,
  setQueryParams
} = quotes.actions;

export default quotes.reducer;

let controller = new AbortController();
const uniqueId = () => Math.random().toString(36).substring(2);

export const fetchQuotes =
  (queryParams: QuoteQueryParams): AppThunk =>
  async (dispatch) => {
    controller.abort();
    controller = new AbortController();
    try {
      const requestId = uniqueId();
      dispatch(getQuotesStart(requestId));
      dispatch(setQueryParams(queryParams));
      const quotes = await searchQuotes(queryParams, controller.signal);
      dispatch(getQuotesSuccess({ quotes, requestId }));
    } catch (err: any) {
      dispatch(getQuotesFailure(err.toString()));
    }
  };

export const fetchRelatedQuotes =
  (quoteId: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getRelatedQuotesStart(uniqueId()));
      const quotes = await getRelatedQuotes(quoteId);
      dispatch(getRelatedQuotesSuccess(quotes));
    } catch (err: any) {
      dispatch(getRelatedQuotesFailure(err.toString()));
    }
  };
